import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { CNPJ_PATTERN, isValidCnpj } from '@/shared/format/cnpj';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { creditNoteFilters } from './credit-note.filters';

// Manifesto da lista (U23/U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. As ações com
// efeito de um registro ficam no manifesto do detalhe; aqui só o lote, preso à seleção da tela.

export const CREDIT_NOTE_LIST_PATH = '/ccb/operacoes';

export const creditNoteOpen = action<{ id: string }>({
  id: 'creditNote.open',
  kind: 'navigate',
  row: true,
  page: true,
  label: msg`Abrir operação`,
  description: msg`Abre uma operação de crédito da lista: cabeçalho com status e etapa, abas e as ações do status. Nada muda só por abrir.`,
  permission: can('CreditNote', 'Read'),
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(`${CREDIT_NOTE_LIST_PATH}/${encodeURIComponent(input.id)}`),
});

export const creditNoteListPage = listPageAction('creditNote.list.page');

const filterKeys = creditNoteFilters.keys.join(', ');

export const creditNoteFilter = filterAction('creditNote.filter', creditNoteFilters, {
  description: msg`Filtra a lista de operações. Filtro omitido é limpo; datas são civis (AAAA-MM-DD); valores em reais (1.000,00) e taxas em percentual (1,99). Sem filtro de data, a lista mostra os últimos 30 dias. Chaves: ${filterKeys}.`,
});

// Lote (trilha "lista"): as ações ficam presas às linhas selecionadas na tela (B2 da revisão da Etapa 3, o
// padrão de `screens/credit-note-list-agent.ts`): a entrada não tem `ids`, `nos` nem `id`, e fora da lista não
// há seleção. A tela troca `run` e `preview` pelos da seleção atual, só quando a ação está visível no lote.
// Assinatura (aprovar/rejeitar) e importar arquivo ficam fora do agente (U24).

/* eslint-disable lingui/no-unlocalized-strings -- respostas e descrições ao modelo, inglês canônico (U33) */
export const LIST_SELECTION_ONLY = 'This action only runs on the credit operation list, on the rows selected there (select them first with creditNote.list.select).';
/* eslint-enable lingui/no-unlocalized-strings */
const needsSelection = () => {
  throw new Error(LIST_SELECTION_ONLY);
};

export const creditNoteListReportInput = z.strictObject({
  cnab: z.string().min(1),
  fundName: z.string().min(1).optional(),
});
export const creditNoteListReport = action<z.infer<typeof creditNoteListReportInput>>({
  id: 'creditNote.list.report',
  kind: 'write',
  label: msg`Gerar relatório`,
  description: msg`Gera um relatório CNAB ou de remessa com as operações selecionadas na lista; o arquivo abre na tela e não volta para o agente. cnab é um dos tipos que o formulário da tela oferece (os configurados para o correspondente ou Cnab400, Cnab400itau, Cnab400B, Cnab444, Cnab444REAG, Cnab500, Cnab550, Cnab600, Cnab800, Cnab600Kovr, CnabDigiMais, Cnab600HeadInvest; na UY3 também liquidationFile, Vortx, CnabAXA, CnabAXACancel; NCB3 com uma nota comercial). fundName é o nome exato do cessionário como a busca da tela lista; só vale para Cnab444, Cnab444REAG, Cnab500, Cnab550, Cnab600 e Cnab600Kovr, e é obrigatório nos quatro primeiros. Não recebe ids: as operações são sempre as linhas selecionadas na tela; para agir em outras, mude a seleção antes. Pede a confirmação da pessoa.`,
  input: creditNoteListReportInput,
  preview: (input) => {
    const cnab = input.cnab;
    return { title: i18n._(msg`Gerar o relatório ${cnab} com as operações selecionadas?`), detail: i18n._(msg`As operações selecionadas na lista.`) };
  },
  run: needsSelection,
});

export const creditNoteListNoInput = z.strictObject({});
export const creditNoteListClone = action({
  id: 'creditNote.list.clone',
  kind: 'write',
  label: msg`Clonar`,
  description: msg`Clona a única operação selecionada na lista (lê os dados dela e cria uma nova igual) e abre a nova. Só com exatamente uma linha selecionada. Não recebe ids: a operação é sempre a linha selecionada na tela; para agir em outra, mude a seleção antes. Pede a confirmação da pessoa.`,
  input: creditNoteListNoInput,
  preview: () => ({ title: i18n._(msg`Clonar a operação selecionada?`), detail: i18n._(msg`Uma nova operação é criada com os mesmos dados.`) }),
  run: needsSelection,
});

export const creditNoteListLinkEmployerInput = z.strictObject({
  employerName: z.string().min(1),
  // eslint-disable-next-line lingui/no-unlocalized-strings -- mensagem ao modelo, inglês canônico (U33)
  employerRegistrationNumber: z.string().regex(CNPJ_PATTERN).refine(isValidCnpj, 'Invalid CNPJ check digits'),
  includeWebCred: z.boolean().optional(),
});
export const creditNoteListLinkEmployer = action<z.infer<typeof creditNoteListLinkEmployerInput>>({
  id: 'creditNote.list.linkEmployer',
  kind: 'write',
  label: msg`Associar empregador`,
  description: msg`Associa um empregador (nome e CNPJ válido, 14 caracteres sem máscara) a todas as operações selecionadas na lista, todas de consignado privado ou público, e, se pedido, envia ao WebCred. Não recebe ids: as operações são sempre as linhas selecionadas na tela; para agir em outras, mude a seleção antes. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'ManualUpdate'),
  input: creditNoteListLinkEmployerInput,
  preview: (input) => {
    const employer = input.employerName;
    return { title: i18n._(msg`Associar o empregador às operações selecionadas?`), detail: i18n._(msg`Empregador: ${employer}.`) };
  },
  run: needsSelection,
});

// Unidade 6B: carteira e cessão em lote, uma requisição com os ids da seleção, como o legado.
export const creditNoteListSendToWallet = action({
  id: 'creditNote.list.sendToWallet',
  kind: 'write',
  label: msg`Enviar para carteira`,
  description: msg`Envia para a Gestão de Carteira, de uma vez, as operações selecionadas na lista. Todas precisam estar em Encerrado e sem cobrança gerada; com alguma fora de Encerrado, a tela trava o envio. Não recebe ids: as operações são sempre as linhas selecionadas na tela; para agir em outras, mude a seleção antes. Pede a confirmação da pessoa.`,
  permission: can('BillingAccount', 'SendToWalletManagement'),
  input: creditNoteListNoInput,
  preview: () => ({ title: i18n._(msg`Enviar para a carteira as operações selecionadas?`), detail: i18n._(msg`As operações selecionadas na lista vão para a Gestão de Carteira.`) }),
  run: needsSelection,
});

export const creditNoteListStartBatchAssignmentInput = z.strictObject({
  sendEmail: z.boolean().optional(),
  ignoreOverDuePayment: z.boolean().optional(),
});
export const creditNoteListStartBatchAssignment = action<z.infer<typeof creditNoteListStartBatchAssignmentInput>>({
  id: 'creditNote.list.startBatchAssignment',
  kind: 'write',
  label: msg`Iniciar cessão em lote`,
  description: msg`Cria uma cessão em lote com as operações selecionadas na lista, todas com cessionário. sendEmail envia o e-mail da cessão (padrão: não); ignoreOverDuePayment ignora as parcelas vencidas (padrão: sim). O cálculo pelo ágio fica só na tela. Não recebe ids: as operações são sempre as linhas selecionadas na tela; para agir em outras, mude a seleção antes. Pede a confirmação da pessoa.`,
  input: creditNoteListStartBatchAssignmentInput,
  preview: () => ({ title: i18n._(msg`Iniciar a cessão em lote com as operações selecionadas?`), detail: i18n._(msg`As operações selecionadas na lista entram numa cessão em lote nova.`) }),
  run: needsSelection,
});

export const creditNoteListScreen = defineScreen({
  id: 'creditNote.list',
  path: CREDIT_NOTE_LIST_PATH,
  title: msg`Operações`,
  description: msg`Lista das operações de crédito, com filtros, paginação e ordem, Nova operação, exportação para Excel e as ações em lote das selecionadas conforme o status (aprovar, rejeitar, enviar para aprovação, cancelar, excluir, gerar relatório, clonar, associar empregador, enviar para carteira, iniciar cessão em lote e outras).`,
  permission: can('CreditNote', 'Read'),
  place: { icon: 'file-text', section: 'credit-note' },
  actions: [creditNoteOpen, creditNoteListPage, creditNoteFilter, creditNoteListReport, creditNoteListClone, creditNoteListLinkEmployer, creditNoteListSendToWallet, creditNoteListStartBatchAssignment],
});

/** O que o agente lê da lista (U24, U46 D2-C), no idioma da tela: contagem, filtros ativos e seleção, sem dado pessoal. */
export function creditNoteListContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly selected: number }): ScreenContext {
  const { total, selected } = input;
  const active = input.filters.join(', ');
  return {
    summary: [
      t`Lista de operações de crédito.`,
      total === undefined ? t`Carregando.` : t`Operações encontradas: ${total}.`,
      input.filters.length > 0 ? t`Filtros: ${active}.` : t`Sem filtros (a lista mostra os últimos 30 dias).`,
      t`Selecionadas: ${selected}.`,
    ].join(' '),
    fields: { total: input.total ?? null, selected: input.selected },
    sensitivity: 'internal',
  };
}
